import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Input,
  Separator,
  toast,
} from '@mlm/ui';
import {
  analyzeImportFile,
  apiErrorMessage,
  executeImportBatch,
  getImportBatch,
  listBranches,
  listImportBatches,
  listProducts,
  type Branch,
  type ImportAnalyzeReport,
  type ImportBatch,
  type ImportBatchSummary,
  type ImportMembershipMapEntry,
  type ImportSalesConfig,
  type ImportSalesProductTarget,
  type Product,
} from '../lib/api';

type Phase = 'upload' | 'review' | 'running';

const issueColor: Record<string, string> = {
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-muted-foreground',
};

export function ImportLegacyDataPage() {
  const [phase, setPhase] = useState<Phase>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [tenant, setTenant] = useState('');
  const [busy, setBusy] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [report, setReport] = useState<ImportAnalyzeReport | null>(null);
  const [recent, setRecent] = useState<ImportBatchSummary[]>([]);

  // execute config
  const [defaultPassword, setDefaultPassword] = useState('');
  const [forceChange, setForceChange] = useState(true);
  const [currency, setCurrency] = useState('PHP');
  const [legSource, setLegSource] = useState<'binary' | 'binary_three'>('binary');
  const [membershipCodes, setMembershipCodes] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  // Opt-in sales import config (null = don't import sales). Set by SalesReviewStep.
  const [salesCfg, setSalesCfg] = useState<ImportSalesConfig | null>(null);

  // running/poll
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    void listImportBatches()
      .then((r) => setRecent(r.batches))
      .catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const hasError = useMemo(() => (report?.issues ?? []).some((i) => i.level === 'error'), [report]);

  async function onAnalyze() {
    if (!file) return;
    setBusy(true);
    try {
      const { importId, report: rep } = await analyzeImportFile(file, tenant.trim() || 'import');
      setBatchId(importId);
      setReport(rep);
      setLegSource(rep.detection.legPointsSource);
      const codes: Record<string, string> = {};
      for (const m of rep.detection.memberships) codes[m.legacyId] = m.suggestedCode;
      setMembershipCodes(codes);
      setPhase('review');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Analysis failed'));
    } finally {
      setBusy(false);
    }
  }

  function startPolling(id: string) {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const b = await getImportBatch(id);
        setBatch(b);
        if (b.status === 'completed' || b.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          if (b.status === 'completed') toast.success('Import completed');
          else toast.error('Import failed');
        }
      } catch {
        /* keep polling */
      }
    };
    void tick();
    pollRef.current = setInterval(() => void tick(), 1500);
  }

  async function onExecute() {
    if (!batchId || !report) return;
    const membershipMap: Record<string, ImportMembershipMapEntry> = {};
    for (const m of report.detection.memberships) {
      membershipMap[m.legacyId] = {
        code: (membershipCodes[m.legacyId] ?? m.suggestedCode).trim().toUpperCase(),
        name: m.suggestedName,
        hierarchy: m.suggestedHierarchy,
        create: true,
      };
    }
    await executeImportBatch(batchId, {
      defaultPassword,
      forceChangePassword: forceChange,
      currency: currency.trim() || 'PHP',
      legPointsSource: legSource,
      membershipMap,
      // Only sent when the admin opted into the sales import; the fingerprint the
      // engine re-derives and refuses on mismatch.
      ...(salesCfg && report.sales
        ? { sales: salesCfg, confirmSales: report.sales.fingerprint }
        : {}),
    });
    setConfirmOpen(false);
    setPhase('running');
    startPolling(batchId);
  }

  function reset() {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase('upload');
    setFile(null);
    setTenant('');
    setBatchId(null);
    setReport(null);
    setBatch(null);
    setDefaultPassword('');
    setSalesCfg(null);
    void listImportBatches()
      .then((r) => setRecent(r.batches))
      .catch(() => {});
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Legacy Data Import</h1>
        <p className="text-sm text-muted-foreground">
          Upload a Firestore <code>.xlsx</code> export from the old system to import members, slots,
          genealogy and current balances. History (sales, payouts, logs) is not imported; no
          commissions are recomputed. Run this on a clean deployment (super-admin user, no slot).
        </p>
      </div>

      {phase === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>1. Upload &amp; analyze</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Tenant label</label>
              <Input
                placeholder="e.g. mhyel"
                value={tenant}
                onChange={(e) => setTenant(e.target.value)}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">Used to scope the import audit trail.</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Export file (.xlsx)</label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block text-sm"
              />
            </div>
            <Button onClick={() => void onAnalyze()} disabled={!file || busy}>
              {busy ? 'Analyzing…' : 'Analyze'}
            </Button>
          </CardContent>
        </Card>
      )}

      {phase === 'review' && report && (
        <ReviewStep
          report={report}
          hasError={hasError}
          membershipCodes={membershipCodes}
          setMembershipCodes={setMembershipCodes}
          defaultPassword={defaultPassword}
          setDefaultPassword={setDefaultPassword}
          forceChange={forceChange}
          setForceChange={setForceChange}
          currency={currency}
          setCurrency={setCurrency}
          legSource={legSource}
          setLegSource={setLegSource}
          showCodes={showCodes}
          setShowCodes={setShowCodes}
          onBack={reset}
          onExecute={() => setConfirmOpen(true)}
          salesSlot={
            report.sales ? <SalesReviewStep report={report} onChange={setSalesCfg} /> : null
          }
        />
      )}

      {phase === 'running' && <RunningStep batch={batch} onReset={reset} />}

      {recent.length > 0 && phase === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Recent imports</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {recent.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between border-b py-1 last:border-0"
              >
                <span>
                  {b.tenant} · {b.fileName ?? '—'}
                </span>
                <Badge variant={b.status === 'completed' ? 'default' : 'secondary'}>
                  {b.status}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Run legacy import"
        description={`This writes ${report?.counts.users ?? 0} members and ${report?.counts.slots ?? 0} slots with their balances. It is idempotent (safe to re-run) but will create real accounts.`}
        confirmLabel="Import"
        confirmVariant="destructive"
        confirmDisabled={defaultPassword.length < 8}
        typedConfirm={{
          expected: 'IMPORT',
          label: 'Type IMPORT to confirm',
          placeholder: 'IMPORT',
        }}
        onConfirm={onExecute}
      >
        {defaultPassword.length < 8 && (
          <p className="text-sm text-red-600">Set a default password (≥ 8 chars) first.</p>
        )}
      </ConfirmDialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function ReviewStep(props: {
  report: ImportAnalyzeReport;
  hasError: boolean;
  membershipCodes: Record<string, string>;
  setMembershipCodes: (v: Record<string, string>) => void;
  defaultPassword: string;
  setDefaultPassword: (v: string) => void;
  forceChange: boolean;
  setForceChange: (v: boolean) => void;
  currency: string;
  setCurrency: (v: string) => void;
  legSource: 'binary' | 'binary_three';
  setLegSource: (v: 'binary' | 'binary_three') => void;
  showCodes: boolean;
  setShowCodes: (v: boolean) => void;
  onBack: () => void;
  onExecute: () => void;
  salesSlot?: React.ReactNode;
}) {
  const { report: r } = props;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>2. Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Members" value={r.counts.users} />
            <Stat
              label="Sponsored leads"
              value={`${r.counts.leadsWithSponsor ?? 0} of ${r.counts.usersWithSponsor ?? 0}`}
            />
            <Stat label="Slots" value={r.counts.slots} />
            <Stat
              label="Placed / unplaced"
              value={`${r.counts.slotsPlaced} / ${r.counts.slotsUnplaced}`}
            />
            <Stat label="Sponsor roots" value={r.counts.sponsorRoots} />
            <Stat label="Cash" value={r.walletTotals.cash} />
            <Stat label="GC" value={r.walletTotals.gc} />
            <Stat label="Token" value={r.walletTotals.token} />
            <Stat label="Coin" value={r.walletTotals.coin} />
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Detected currencies:</span>{' '}
            {r.detection.currencies.join(', ')} · <span className="text-muted-foreground">PV:</span>{' '}
            {r.detection.hasPersonalPv ? 'personal' : '—'}/{r.detection.hasGroupPv ? 'group' : '—'}
          </div>
        </CardContent>
      </Card>

      {r.issues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Validation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {r.issues.map((i, idx) => (
              <div key={idx} className={issueColor[i.level]}>
                [{i.level}] {i.message}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Membership mapping</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Each legacy tier becomes (or maps to) a membership with this code. Edit if a matching
            tier already exists in this deployment.
          </p>
          {r.detection.memberships.map((m) => (
            <div key={m.legacyId} className="flex items-center gap-3 text-sm">
              <span className="w-44 shrink-0">
                {m.legacyId} <span className="text-muted-foreground">×{m.count}</span>
              </span>
              <span className="text-muted-foreground">→</span>
              <Input
                value={props.membershipCodes[m.legacyId] ?? m.suggestedCode}
                onChange={(e) =>
                  props.setMembershipCodes({
                    ...props.membershipCodes,
                    [m.legacyId]: e.target.value,
                  })
                }
                className="max-w-xs font-mono"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>3. Import settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Default password (all members)</label>
            <Input
              type="text"
              value={props.defaultPassword}
              onChange={(e) => props.setDefaultPassword(e.target.value)}
              placeholder="≥ 8 characters"
              className="max-w-xs"
            />
            <p className="text-xs text-muted-foreground">
              Every imported member gets this password; they're prompted to change it on first
              login.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={props.forceChange}
              onCheckedChange={(v) => props.setForceChange(v === true)}
            />
            Force password change on first login
          </label>
          <div className="flex flex-wrap gap-6">
            <div className="space-y-1">
              <label className="text-sm font-medium">Cash currency label</label>
              <Input
                value={props.currency}
                onChange={(e) => props.setCurrency(e.target.value)}
                className="w-28"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Binary leg points source</label>
              <select
                value={props.legSource}
                onChange={(e) => props.setLegSource(e.target.value as 'binary' | 'binary_three')}
                className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="binary">binary (binary_points_left/right)</option>
                <option value="binary_three">binary_three (binary_three_points_left/right)</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Unused codes ({r.unusedCodes.length}){' '}
            <span className="text-xs font-normal text-muted-foreground">
              — listed only, not imported
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <Button variant="ghost" size="sm" onClick={() => props.setShowCodes(!props.showCodes)}>
            {props.showCodes ? 'Hide' : 'Show'} codes
          </Button>
          {props.showCodes && (
            <div className="mt-2 max-h-64 overflow-auto font-mono text-xs">
              {r.unusedCodes.map((c) => (
                <div key={c.code}>
                  {c.code} / {c.pin ?? '—'} · {c.membership ?? ''}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {props.salesSlot}

      <Separator />
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={props.onBack}>
          ← Start over
        </Button>
        <Button
          variant="destructive"
          disabled={props.hasError || props.defaultPassword.length < 8}
          onClick={props.onExecute}
        >
          Run import
        </Button>
      </div>
      {props.hasError && (
        <p className="text-right text-sm text-red-600">
          Resolve the [error] issues above before importing.
        </p>
      )}
    </div>
  );
}

/**
 * Opt-in legacy SALES import review. Collapsed + off by default. Maps each selling
 * outlet to an existing branch + each legacy sku to a live product, then feeds an
 * `ImportSalesConfig` (or null when disabled) up to the page for execute.
 */
function SalesReviewStep({
  report,
  onChange,
}: {
  report: ImportAnalyzeReport;
  onChange: (cfg: ImportSalesConfig | null) => void;
}) {
  const sales = report.sales!;
  const [enabled, setEnabled] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [branchMap, setBranchMap] = useState<
    Record<string, { branchId: string; series: 'preserve' | 'renumber' }>
  >({});
  const [productMap, setProductMap] = useState<Record<string, ImportSalesProductTarget>>({});
  const [unmatchedSku, setUnmatchedSku] = useState<'block' | 'placeholder' | 'skip'>('block');

  // Fetch branches + full catalog once the admin opens the section.
  useEffect(() => {
    if (!enabled) return;
    void listBranches()
      .then(setBranches)
      .catch(() => {});
    void listProducts({ pageSize: 1000 })
      .then(setProducts)
      .catch(() => {});
  }, [enabled]);

  // Seed defaults once the branch list + catalog arrive: seller → Main branch
  // (preserve series for the Main outlet, renumber for the rest); sku → exact
  // sku match if one exists in the live catalog.
  useEffect(() => {
    if (!enabled || branches.length === 0) return;
    const main = branches.find((b) => b.isMain) ?? branches[0];
    setBranchMap((prev) => {
      const next = { ...prev };
      for (const s of sales.sellers) {
        if (!next[s.code]) {
          next[s.code] = {
            branchId: main?.id ?? '',
            series: s === sales.sellers[0] ? 'preserve' : 'renumber',
          };
        }
      }
      return next;
    });
  }, [enabled, branches, sales.sellers]);

  useEffect(() => {
    if (!enabled || products.length === 0) return;
    const bySku = new Map(products.map((p) => [p.sku.toUpperCase(), p.id]));
    setProductMap((prev) => {
      const next = { ...prev };
      for (const s of sales.skus) {
        if (!next[s.sku]) {
          const pid = bySku.get(s.sku.toUpperCase());
          if (pid) next[s.sku] = { kind: 'product', productId: pid };
        }
      }
      return next;
    });
  }, [enabled, products, sales.skus]);

  // Emit the config (or null) whenever inputs change.
  useEffect(() => {
    if (!enabled) {
      onChange(null);
      return;
    }
    onChange({ branchMap, productMap, unmatchedSku, importPayments: true });
  }, [enabled, branchMap, productMap, unmatchedSku, onChange]);

  const mappedSkuCount = sales.skus.filter((s) => productMap[s.sku]).length;
  const unmappedSellers = sales.sellers.filter((s) => !branchMap[s.code]?.branchId).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sales history (optional)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This export contains <strong>{sales.count}</strong> legacy sale(s) (#{sales.minSaleId}–#
          {sales.maxSaleId}). They are <strong>NOT</strong> imported unless you enable it here. No
          commissions are recomputed — legacy earnings are already opening balances. Imported orders
          are inert history (never re-pay, never mint kit codes).
        </div>

        <label className="flex items-center gap-2 text-sm font-medium">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(Boolean(v))} />
          Import sales history ({sales.count} orders)
        </label>

        {enabled && (
          <div className="space-y-5">
            {/* Seller → branch mapping */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Selling outlet → branch</p>
              {sales.sellers.map((s) => (
                <div
                  key={s.code}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm"
                >
                  <span className="font-mono">
                    {s.code} {s.branchName ? `· ${s.branchName}` : ''}{' '}
                    <span className="text-muted-foreground">({s.count})</span>
                  </span>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={branchMap[s.code]?.branchId ?? ''}
                    onChange={(e) =>
                      setBranchMap((m) => ({
                        ...m,
                        [s.code]: {
                          branchId: e.target.value,
                          series: m[s.code]?.series ?? 'preserve',
                        },
                      }))
                    }
                  >
                    <option value="">— map to branch —</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} {b.isMain ? '(Main)' : ''}
                      </option>
                    ))}
                  </select>
                  <select
                    className="rounded border px-2 py-1 text-sm"
                    value={branchMap[s.code]?.series ?? 'preserve'}
                    onChange={(e) =>
                      setBranchMap((m) => ({
                        ...m,
                        [s.code]: {
                          branchId: m[s.code]?.branchId ?? '',
                          series: e.target.value as 'preserve' | 'renumber',
                        },
                      }))
                    }
                  >
                    <option value="preserve">Keep legacy #</option>
                    <option value="renumber">Restart at 1</option>
                  </select>
                </div>
              ))}
              {unmappedSellers > 0 && (
                <p className="text-xs text-red-600">
                  {unmappedSellers} outlet(s) still need a branch.
                </p>
              )}
            </div>

            {/* Status preview */}
            <div className="text-sm">
              <p className="font-medium">Status → order status</p>
              <p className="text-xs text-muted-foreground">
                {Object.entries(sales.statuses)
                  .map(([k, v]) => `${k} (${v})`)
                  .join(', ')}{' '}
                — all imported as terminal completed/cancelled history.
              </p>
            </div>

            {/* Unmatched SKU policy */}
            <div className="space-y-1 text-sm">
              <p className="font-medium">
                Product mapping — {mappedSkuCount}/{sales.skus.length} matched
              </p>
              <label className="flex items-center gap-2 text-xs">
                <span>Unmatched skus:</span>
                <select
                  className="rounded border px-2 py-1"
                  value={unmatchedSku}
                  onChange={(e) =>
                    setUnmatchedSku(e.target.value as 'block' | 'placeholder' | 'skip')
                  }
                >
                  <option value="block">Block until all mapped (recommended)</option>
                  <option value="placeholder">Create inactive placeholder</option>
                  <option value="skip">Skip the line</option>
                </select>
              </label>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded border p-2">
                {sales.skus.map((s) => (
                  <div key={s.sku} className="grid grid-cols-[1fr_auto] items-center gap-2">
                    <span
                      className="truncate font-mono text-xs"
                      title={`${s.sku} — ${s.name ?? ''}`}
                    >
                      {s.sku} <span className="text-muted-foreground">({s.qty})</span>
                    </span>
                    <select
                      className="rounded border px-2 py-1 text-xs"
                      value={
                        productMap[s.sku]?.kind === 'product'
                          ? (productMap[s.sku] as { productId: string }).productId
                          : ''
                      }
                      onChange={(e) =>
                        setProductMap((m) => ({
                          ...m,
                          ...(e.target.value
                            ? { [s.sku]: { kind: 'product', productId: e.target.value } }
                            : {}),
                        }))
                      }
                    >
                      <option value="">— unmatched —</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {sales.unresolvedBuyers > 0 && (
              <p className="text-xs text-red-600">
                {sales.unresolvedBuyers} sale(s) have a buyer not among imported members — the
                import will fail closed. Import the members first.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunningStep({ batch, onReset }: { batch: ImportBatch | null; onReset: () => void }) {
  const status = batch?.status ?? 'importing';
  const progress = batch?.report?.progress;
  const recon = batch?.report?.reconciliation;
  return (
    <Card>
      <CardHeader>
        <CardTitle>4. Import {status}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === 'importing' && (
          <div className="text-sm">
            {progress ? (
              <span>
                {progress.phase}: {progress.done} / {progress.total}
              </span>
            ) : (
              <span>Starting…</span>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Wallet balances appear once the worker materializer drains the opening-balance ledger.
            </p>
          </div>
        )}
        {status === 'failed' && (
          <p className="text-sm text-red-600">Failed: {batch?.error ?? 'unknown error'}</p>
        )}
        {status === 'completed' && recon && (
          <div className="space-y-1 text-sm">
            <div className="font-medium text-green-700">Reconciliation</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs sm:grid-cols-3">
              {Object.entries(recon)
                .filter(([k]) => k !== 'orphans')
                .map(([k, v]) => (
                  <div key={k}>
                    {k}: {String(v)}
                  </div>
                ))}
            </div>
          </div>
        )}
        {(status === 'completed' || status === 'failed') && <Button onClick={onReset}>Done</Button>}
      </CardContent>
    </Card>
  );
}
